import type * as React from 'react'
import type { Button as BaseButton } from '@base-ui/react/button'
import { createLink } from '@tanstack/react-router'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'
import { ButtonPrimitive } from '@/components/ui/button-primitive'

const buttonVariants = cva(
  "group/button inline-flex shrink-0 cursor-pointer items-center justify-center rounded-lg border border-transparent bg-clip-padding text-sm font-medium whitespace-nowrap transition-all outline-none select-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 active:not-aria-[haspopup]:translate-y-px disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/80',
        outline:
          'border-border bg-background hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground dark:border-input dark:bg-input/30 dark:hover:bg-input/50',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-[color-mix(in_oklch,var(--secondary),var(--foreground)_5%)] aria-expanded:bg-secondary aria-expanded:text-secondary-foreground',
        ghost:
          'hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground dark:hover:bg-muted/50',
        destructive:
          'bg-destructive/10 text-destructive hover:bg-destructive/20 focus-visible:border-destructive/40 focus-visible:ring-destructive/20 dark:bg-destructive/20 dark:hover:bg-destructive/30 dark:focus-visible:ring-destructive/40',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        'default': 'h-8 gap-1.5 px-2.5 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2',
        'xs': "h-6 gap-1 rounded-[min(var(--radius-md),10px)] px-2 text-xs in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3",
        'sm': "h-7 gap-1 rounded-[min(var(--radius-md),12px)] px-2.5 text-[0.8rem] in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3.5",
        'lg': 'h-9 gap-1.5 px-2.5 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2',
        'icon': 'size-8',
        'icon-xs':
          "size-6 rounded-[min(var(--radius-md),10px)] in-data-[slot=button-group]:rounded-lg [&_svg:not([class*='size-'])]:size-3",
        'icon-sm': 'size-7 rounded-[min(var(--radius-md),12px)] in-data-[slot=button-group]:rounded-lg',
        'icon-lg': 'size-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

function Button({
  className,
  variant = 'default',
  size = 'default',
  disabled,
  ...props
}: BaseButton.Props & VariantProps<typeof buttonVariants>) {
  // This layer is appearance only. The hydration gate — and the suppression of `disabled:` styling while
  // a button is inert purely for hydration — both live in ButtonPrimitive, so `disabled:opacity-50` below
  // applies exactly when the CALLER disabled the button and never flashes on a freshly served page.
  return (
    <ButtonPrimitive
      data-slot="button"
      disabled={disabled}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

/**
 * The anchor half of `Button`: something that navigates but LOOKS like a button. Use it for external or
 * non-router hrefs; use `ButtonLink` for in-app destinations.
 *
 * Deliberately NOT `<Button render={<a/>}>`. base-ui's Button asserts in dev that it actually rendered a
 * native `<button>` (`nativeButton` defaults to true) and warns when it didn't, and its escape hatch —
 * `nativeButton={false}` — stamps `role="button"` over the anchor, which is worse: the control stops being
 * a link to assistive tech and to `getByRole('link')`. An anchor also has nothing to gain from
 * ButtonPrimitive's hydration gate, because a click before hydration is a real document navigation rather
 * than a dropped listener. So the only thing the button layer has to contribute here is its classes.
 */
function ButtonAnchor({
  className,
  variant = 'default',
  size = 'default',
  ...props
}: React.ComponentProps<'a'> & VariantProps<typeof buttonVariants>) {
  return <a data-slot="button" className={cn(buttonVariants({ variant, size, className }))} {...props} />
}

/** `ButtonAnchor` wired to the router, so `to`/`params`/`search` stay as type-safe as on `Link`. */
const ButtonLink = createLink(ButtonAnchor)

export { Button, ButtonAnchor, ButtonLink, buttonVariants }
